/**
 * useRecaptcha — generate + verify a Google reCAPTCHA v3 token (GR-004/005/008/012).
 *
 * Returns a `verify(action)` function to call at submit time. It:
 *   1. Skips entirely when reCAPTCHA is disabled/unconfigured for the site (GR-001),
 *      so forms keep working when the feature is off.
 *   2. Generates a token for the given action via `executeRecaptcha` (GR-004).
 *   3. POSTs the token to `/server/verify-recaptcha` as `c_recaptchaToken` (GR-005),
 *      where it is verified against Google and scored (GR-008/010/011).
 *   4. On a below-threshold/invalid result, returns a localized GR-012 message
 *      ("Google reCaptcha failed with score <score>") for display beneath the form.
 *
 * Verification fails OPEN on infrastructure errors (token generation or network
 * failures) so that a Google/reCAPTCHA outage never blocks legitimate shoppers —
 * reCAPTCHA is an additional security layer, not the primary auth gate. Requests
 * are only rejected when Google explicitly returns a low/failed score.
 */
import {useCallback} from 'react';
import {defineMessage, useIntl} from 'react-intl';
import {useGoogleReCaptcha} from 'react-google-recaptcha-v3';

import {useSitePreference} from '@amblique/base/app/hooks/use-site-preference';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';

import {isRecaptchaEnabledValue} from '@amblique/king-living/app/components/recaptcha';
import {
  RECAPTCHA_PREFERENCE_KEYS as KEYS,
  RECAPTCHA_VERIFY_ENDPOINT,
} from '@amblique/king-living/app/constants/recaptcha';

// GR-012: exact wording required by the TSD. {score} is filled from Google's response.
const RECAPTCHA_FAILED_MESSAGE = defineMessage({
  id: 'recaptcha.error.failed',
  defaultMessage: 'Google reCaptcha failed with score {score}',
});

export const useRecaptcha = () => {
  const {executeRecaptcha} = useGoogleReCaptcha();
  const {site} = useMultiSite();
  const {formatMessage} = useIntl();
  const enabled = isRecaptchaEnabledValue(useSitePreference(KEYS.ENABLED, false));
  const siteId = site?.id || 'kl-au';

  const verify = useCallback(
    async (action) => {
      // GR-001: feature off (or provider not mounted) → do not verify, allow through.
      if (!enabled || typeof executeRecaptcha !== 'function') {
        return {success: true, skipped: true};
      }

      let token;

      try {
        token = await executeRecaptcha(action);
      } catch {
        // Token generation failed (script blocked / offline) → fail open.
        return {success: true, skipped: true};
      }

      try {
        const response = await fetch(RECAPTCHA_VERIFY_ENDPOINT, {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({c_recaptchaToken: token, action, siteId}),
        });

        const data = await response.json().catch(() => ({}));

        if (response.ok && data?.success) {
          return {success: true, score: data.score, skipped: Boolean(data.skipped)};
        }

        // GR-012: rejected — build the localized failure message with the score.
        const score = typeof data?.score === 'number' ? data.score : 'N/A';

        return {
          success: false,
          score: data?.score,
          message: formatMessage(RECAPTCHA_FAILED_MESSAGE, {score}),
        };
      } catch {
        // Network/parse error talking to our own endpoint → fail open.
        return {success: true, skipped: true};
      }
    },
    [enabled, executeRecaptcha, siteId, formatMessage]
  );

  return {verify, enabled};
};
